import type { ApiErrorDetail } from '@diagon-alley/common';
import {
    Box,
    Button,
    Flex,
    Loader,
    type ButtonProps,
    type PolymorphicComponentProps,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    CheckCircleIcon,
    InfoIcon,
    WarningCircleIcon,
    WarningIcon,
    type Icon,
} from '@phosphor-icons/react';
import { useCallback, useRef, type ReactNode } from 'react';
import { v4 as uuid } from 'uuid';

type ToastNotificationData = Omit<
    Parameters<typeof notifications.show>[0],
    'message' | 'key'
> & {
    key?: string;
    subtitle?: string | ReactNode;
    action?: PolymorphicComponentProps<'button', ButtonProps> & {
        icon?: Icon;
    };
    optionalAction?: PolymorphicComponentProps<'button', ButtonProps> & {
        icon?: Icon;
    };
    info?: string | ReactNode;
};

const useToaster = () => {
    const openedKeys = useRef(new Set<string>());
    const showToast = useCallback(
        ({
            key = uuid(),
            subtitle,
            action,
            color: toastColor,
            autoClose = 5000,
            optionalAction,
            info,
            ...rest
        }: ToastNotificationData) => {
            const commonProps = {
                autoClose,
                styles: {
                    title: {
                        color: 'rgb(var(--color-gray-800))',
                        fontWeight: 600,
                        marginBottom: '0.5rem',
                    },
                    closeButton: {
                        ':hover': {
                            background: 'rgb(--color-shade-4)',
                        },
                        color: 'rgb(var(--color-gray-700))',
                        display: 'flex',
                        height: '1.1rem',
                        borderRadius: '0.5rem',
                    },
                    icon: {
                        backgroundColor: 'white',
                        marginRight: 0,
                        height: '1.25rem',
                    },

                    root: {
                        border: '1px solid',
                        borderColor: 'rgb(var(--color-gray-250))',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'start',
                        alignItems: 'start',
                        padding: '1rem',
                        paddingBottom: '0.5rem',
                        maxHeight: '100% !important',
                    },
                    loader: {
                        marginRight: '0.4rem !important',
                        width: '1rem !important',
                        height: '1.25rem !important',
                    },
                },

                message: (
                    <Flex
                        direction="column"
                        align="flex-start"
                        gap={8}
                        className={
                            !subtitle && !info && !action && !optionalAction
                                ? ''
                                : 'pb-2'
                        }
                    >
                        {subtitle && (
                            <Box className="text-xs text-gray-600">
                                {subtitle}
                            </Box>
                        )}

                        {info && (
                            <Box className="rounded-lg bg-gray-100 p-2 text-xs text-gray-600">
                                {info}
                            </Box>
                        )}
                        <Flex gap={12} align="center">
                            {action && (
                                <Button
                                    {...action}
                                    variant="subtle"
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>,
                                    ) => {
                                        notifications.hide(key);
                                        action.onClick?.(e);
                                    }}
                                />
                            )}
                            {optionalAction && (
                                <Button
                                    {...optionalAction}
                                    variant="subtle"
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>,
                                    ) => {
                                        notifications.hide(key);
                                        optionalAction.onClick?.(e);
                                    }}
                                />
                            )}
                        </Flex>
                    </Flex>
                ),
                onClose: () => {
                    openedKeys.current.delete(key);
                },
            };

            const method = openedKeys.current.has(key) ? 'update' : 'show';

            if (method === 'show') {
                openedKeys.current.add(key);
            }
            notifications[method]({
                id: key,
                ...commonProps,
                ...rest,
            });
        },
        [],
    );

    const showToastSuccess = useCallback(
        (props: ToastNotificationData) => {
            showToast({
                color: '',
                icon: (
                    <CheckCircleIcon
                        color="rgb(var(--color-green))"
                        weight="duotone"
                    />
                ),
                ...props,
            });
        },
        [showToast],
    );
    const showToastError = useCallback(
        (props: ToastNotificationData) => {
            showToast({
                icon: (
                    <WarningCircleIcon
                        color="rgb(var(--color-halt-800))"
                        weight="duotone"
                    />
                ),
                autoClose: 5000,
                ...props,
            });
        },
        [showToast],
    );

    const showToastApiError = useCallback(
        (
            props: Omit<ToastNotificationData, 'subtitle'> & {
                apiError: ApiErrorDetail | undefined;
            },
        ) => {
            const title: ReactNode | undefined = props.title ?? 'Error';
            const info: ReactNode | undefined =
                props?.apiError?.data.error ?? props?.apiError?.message;

            showToast({
                icon: (
                    <WarningCircleIcon
                        color="rgb(var(--color-halt-800))"
                        weight="duotone"
                    />
                ),
                autoClose: 5000,
                title,
                ...(info && { info }),
                ...props,
            });
        },
        [showToast],
    );

    const showToastInfo = useCallback(
        (props: ToastNotificationData) => {
            showToast({
                icon: (
                    <InfoIcon
                        color="rgb(var(--color-gray-600))"
                        weight="duotone"
                    />
                ),
                ...props,
            });
        },
        [showToast],
    );

    const showToastWarning = useCallback(
        (props: ToastNotificationData) => {
            showToast({
                icon: (
                    <WarningIcon
                        color="rgb(var(--color-mustard-800))"
                        weight="duotone"
                    />
                ),
                ...props,
            });
        },
        [showToast],
    );

    const showToastLoading = useCallback(
        (props: ToastNotificationData) => {
            showToast({
                ...props,
                icon: <Loader color="rgb(var(--color-blu-800))" size={14} />,
            });
        },
        [showToast],
    );

    const closeToast = useCallback((key: string) => {
        notifications.hide(key);
    }, []);

    return {
        showToastSuccess,
        showToastApiError,
        showToastError,
        showToastInfo,
        showToastWarning,
        closeToast,
        showToastLoading,
    };
};

export default useToaster;
